/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * JIRA Integration
 * 
 * Handles JIRA issue and user story import/export.
 */

import { getSkillShopServerBaseUrl } from './base';
import type { AcceptanceCriterion } from '@shared/types/user-stories';

export interface JiraConfig {
  url: string;
  username: string;
  apiToken: string;
  projectKey: string;
  issueType?: string;
  epicIssueType?: string;
}

export interface JiraImportResult {
  total: number;
  successful: number;
  failed: number;
  updated: number;
  created: number;
  skipped: number;
  errors: string[];
}

export interface JiraExportResult {
  successful: Array<{ item: any; key: string; type: 'epic' | 'story'; action: 'created' | 'updated' }>;
  failed: Array<{ item: any; error: string; type: 'epic' | 'story' }>;
}

/**
 * Test JIRA connection
 * SkillShop server endpoint - uses direct fetch
 */
export async function testJiraConnection(config: {
  url: string;
  username: string;
  apiToken: string;
  projectKey: string;
}): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${getSkillShopServerBaseUrl()}/api/jira/import/test-connection`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config)
    });

    const data = await response.json();
    
    if (data.success && data.data?.success) {
      return { success: true };
    }
    return { 
      success: false, 
      error: data.data?.error || data.error?.message || 'Connection failed' 
    };
  } catch (err: any) {
    return { 
      success: false, 
      error: err.message || 'Failed to connect to server' 
    };
  }
}

/**
 * Test JIRA connection for user stories
 * SkillShop server endpoint - uses direct fetch
 */
export async function testJiraUserStoryConnection(config: {
  url: string;
  username: string;
  apiToken: string;
  projectKey: string;
}): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${getSkillShopServerBaseUrl()}/api/jira/user-stories/test-connection`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config)
    });

    const data = await response.json();
    
    if (data.success && data.data?.success) {
      return { success: true };
    }
    return { 
      success: false, 
      error: data.data?.error || data.error?.message || 'Connection failed' 
    };
  } catch (err: any) {
    return { 
      success: false, 
      error: err.message || 'Failed to connect to server' 
    };
  }
}

/**
 * Preview JIRA issue import
 * SkillShop server endpoint - uses direct fetch
 */
export async function previewJiraIssueImport(config: {
  url: string;
  username: string;
  apiToken: string;
  projectKey: string;
}, options?: {
  jql?: string;
  maxResults?: number;
}): Promise<{ success: boolean; data?: any; error?: string }> {
  try {
    const response = await fetch(`${getSkillShopServerBaseUrl()}/api/jira/import/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        config,
        jql: options?.jql,
        maxResults: options?.maxResults || 100
      })
    });

    const data = await response.json();
    
    if (data.success) {
      return { success: true, data: data.data };
    }
    return { success: false, error: data.error?.message || 'Failed to load preview' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to load preview' };
  }
}

/**
 * Import JIRA issues
 * SkillShop server endpoint - uses direct fetch
 */
export async function importJiraIssues(config: {
  url: string;
  username: string;
  apiToken: string;
  projectKey: string;
}, options?: {
  jql?: string;
  maxResults?: number;
  updateExisting?: boolean;
  repoUrl?: string;
  repoPath?: string;
}): Promise<{ success: boolean; data?: JiraImportResult; error?: string }> {
  try {
    const response = await fetch(`${getSkillShopServerBaseUrl()}/api/jira/import/import`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        config,
        jql: options?.jql,
        maxResults: options?.maxResults || 100,
        updateExisting: options?.updateExisting || false,
        repoUrl: options?.repoUrl,
        repoPath: options?.repoPath
      })
    });

    const data = await response.json();
    
    if (data.success) {
      return { success: true, data: data.data };
    }
    return { success: false, error: data.error?.message || 'Import failed' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Import failed' };
  }
}

/**
 * Preview JIRA user story import
 * SkillShop server endpoint - uses direct fetch
 */
export async function previewJiraUserStoryImport(config: {
  url: string;
  username: string;
  apiToken: string;
  projectKey: string;
  epicIssueType?: string;
  issueType?: string;
}, options?: {
  maxResults?: number;
}): Promise<{ success: boolean; data?: any; error?: string }> {
  try {
    const response = await fetch(`${getSkillShopServerBaseUrl()}/api/jira/user-stories/preview-import`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        config,
        maxResults: options?.maxResults || 100
      })
    });

    const data = await response.json();
    
    if (data.success) {
      return { success: true, data: data.data };
    }
    
    // Check if this is a "not implemented" error
    if (response.status === 404) {
      return { 
        success: false, 
        error: 'User story import is not yet implemented. This feature will be available in a future update.' 
      };
    }
    
    return { success: false, error: data.error?.message || 'Failed to load preview' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to load preview. The import endpoint may not be implemented yet.' };
  }
}

/**
 * Import JIRA user stories
 * SkillShop server endpoint - uses direct fetch
 */
export async function importJiraUserStories(config: {
  url: string;
  username: string;
  apiToken: string;
  projectKey: string;
  epicIssueType?: string;
  issueType?: string;
}, options?: {
  maxResults?: number;
  updateExisting?: boolean;
  repoUrl?: string;
  repoPath?: string;
}): Promise<{ success: boolean; data?: any; error?: string }> {
  try {
    const response = await fetch(`${getSkillShopServerBaseUrl()}/api/jira/user-stories/import`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        config,
        maxResults: options?.maxResults || 100,
        updateExisting: options?.updateExisting || false,
        repoUrl: options?.repoUrl,
        repoPath: options?.repoPath
      })
    });

    const data = await response.json();
    
    if (data.success) {
      return { success: true, data: data.data };
    }
    return { success: false, error: data.error?.message || 'Import failed' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Import failed' };
  }
}

/**
 * Get JIRA projects
 * SkillShop server endpoint - uses direct fetch
 */
export async function getJiraProjects(config: {
  url: string;
  username: string;
  apiToken: string;
  projectKey?: string;
}): Promise<{ success: boolean; data?: Array<{ key: string; name: string }>; error?: string }> {
  try {
    const response = await fetch(`${getSkillShopServerBaseUrl()}/api/jira/user-stories/projects`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        url: config.url,
        username: config.username,
        apiToken: config.apiToken,
        projectKey: config.projectKey
      })
    });

    const data = await response.json();
    
    if (data.success || data.data) {
      return { success: true, data: data.data || [] };
    }
    return { success: false, error: data.error?.message || 'Failed to fetch projects' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to fetch projects' };
  }
}

/**
 * Get JIRA issue types for a project
 * SkillShop server endpoint - uses direct fetch
 */
export async function getJiraIssueTypes(config: {
  url: string;
  username: string;
  apiToken: string;
  projectKey: string;
}): Promise<{ success: boolean; data?: Array<{ id: string; name: string }>; error?: string }> {
  try {
    const response = await fetch(`${getSkillShopServerBaseUrl()}/api/jira/user-stories/projects/${encodeURIComponent(config.projectKey)}/issue-types`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        url: config.url,
        username: config.username,
        apiToken: config.apiToken,
        projectKey: config.projectKey
      })
    });

    const data = await response.json();
    
    if (data.success || data.data) {
      return { success: true, data: data.data || [] };
    }
    return { success: false, error: data.error?.message || 'Failed to fetch issue types' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to fetch issue types' };
  }
}

/**
 * Export user stories to JIRA
 * SkillShop server endpoint - uses direct fetch
 */
export async function exportUserStoriesToJira(config: {
  url: string;
  username: string;
  apiToken: string;
  projectKey: string;
  issueType?: string;
  epicIssueType?: string;
}, epics: Array<{
  id: string;
  title: string;
  description?: string;
  userStoryIds?: string[];
  section?: string;
  status?: string;
}>, stories: Array<{
  id: string;
  title: string;
  role?: string;
  goal?: string;
  benefit?: string;
  acceptanceCriteria?: AcceptanceCriterion[];
  section?: string;
  epicId?: string;
  status?: string;
}>): Promise<{ success: boolean; data?: JiraExportResult; error?: string }> {
  try {
    const response = await fetch(`${getSkillShopServerBaseUrl()}/api/jira/user-stories/export`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ config, epics, stories })
    });

    const data = await response.json();

    if (data.success) {
      return { success: true, data: data.data };
    }
    return { success: false, error: data.error?.message || 'Export failed' };
  } catch (err: any) {
    return { success: false, error: err.message || 'Export failed' };
  }
}

